import { FC, Fragment } from "react";
import {
  IconButton,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  CommitmentResourceType,
  ProviderType,
} from "@/services/cloudchipr.api";

import { useAppSelector } from "@/store/hooks";
import { isResourceTypeInReservationsCoverageSelector } from "@/store/commitments/selectors/common/isResourceTypeInReservationsCoverageSelector";
import { isResourceTypeInSavingsPlansCoverageSelector } from "@/store/commitments/selectors/common/isResourceTypeInSavingsPlansCoverageSelector";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { commitmentsDatesSelector } from "@/store/commitments/selectors/common/view-options/commitmentsDatesSelector";
import { getCommitmentsResourceUrl } from "@/components/pages/commtiments/overview/content/utils/helpers/getCommitmentsResourceUrl";

interface CommitmentsOverviewCoverageTableViewInCoverageLinkProps {
  resourceType: CommitmentResourceType;
  hovered?: boolean;
}

export const CommitmentsOverviewCoverageTableViewCoverageLink: FC<
  CommitmentsOverviewCoverageTableViewInCoverageLinkProps
> = ({ resourceType, hovered }) => {
  const { anchor, openMenu, closeMenu, open } = useMenuHook();
  const params = useParams();
  const provider = params?.provider as ProviderType;
  const orgId = params?.orgId ?? "";

  const dates = useAppSelector(commitmentsDatesSelector);
  const isResourceInReservations = useAppSelector((state) =>
    isResourceTypeInReservationsCoverageSelector(state, provider, resourceType),
  );
  const isResourceInSavingsPlans = useAppSelector((state) =>
    isResourceTypeInSavingsPlansCoverageSelector(state, provider, resourceType),
  );
  const isResourceInBoth = isResourceInSavingsPlans && isResourceInReservations;

  const coverageUrlActiveTab = isResourceInReservations
    ? "reservation"
    : "savings_plan";

  const viewRiCoverageUrl = getCommitmentsResourceUrl(
    provider,
    orgId,
    "reservation",
    "coverage",
    resourceType,
    dates,
  );
  const viewSpCoverageUrl = getCommitmentsResourceUrl(
    provider,
    orgId,
    "savings_plan",
    "coverage",
    resourceType,
    dates,
  );
  const viewCoverageUrl =
    coverageUrlActiveTab === "reservation"
      ? viewRiCoverageUrl
      : viewSpCoverageUrl;

  return (
    <Fragment>
      {hovered && !isResourceInBoth && (
        <Tooltip title="View Coverage" placement="right">
          <IconButton
            component={RouterLink}
            to={viewCoverageUrl}
            target="_blank"
            size="small"
            sx={{ p: 0, "&:hover": { bgcolor: "transparent" } }}
          >
            <OpenInNewIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      )}

      {(hovered || open) && isResourceInBoth && (
        <Tooltip title="View Coverage" placement="right">
          <IconButton
            onClick={openMenu}
            size="small"
            sx={{ p: 0, "&:hover": { bgcolor: "transparent" } }}
          >
            <OpenInNewIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      )}

      <Menu
        open={open}
        onClose={closeMenu}
        anchorEl={anchor}
        anchorOrigin={{
          vertical: "top",
          horizontal: 30,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Link
          component={RouterLink}
          onClick={closeMenu}
          to={viewRiCoverageUrl}
          target="_blank"
          sx={{ textDecoration: "none", color: "inherit" }}
        >
          <MenuItem>
            <ListItemText primary="View RI Coverage" />
          </MenuItem>
        </Link>
        <Link
          component={RouterLink}
          onClick={closeMenu}
          to={viewSpCoverageUrl}
          target="_blank"
          sx={{ textDecoration: "none", color: "inherit" }}
        >
          <MenuItem>
            <ListItemText primary="View SP Coverage" />
          </MenuItem>
        </Link>
      </Menu>
    </Fragment>
  );
};
